<template>
    <main v-if="contentIsReady" class="tag-page">
        <TagPageHero :tag="humanReadableTag" />
        <SliceZone v-if="reinsuranceSlice" :slices="[reinsuranceSlice]" :components="components" />
        <TagPageProfiles
            v-if="landingPage?.profileList?.content && hasElements"
            :tag="humanReadableTag"
            :profiles="landingPage?.profileList?.content"
            :total="totalElements"
        />
        <div v-else class="slice-content">
            <div class="tag-page__no-profiles">
                <h2>{{ $t('landing.tag.no.profiles.found', {0: tag}) }}</h2>
            </div>
        </div>
        <TagPageFooter v-if="pagination" :tag="humanReadableTag" :pagination="pagination" />
        <SliceZone v-if="prismicPage" :slices="prismicPage.data.slices" :components="components" />
    </main>
</template>

<script setup lang="ts">
    import {ref, provide, computed} from 'vue';
    import type {Slice} from '@prismicio/types';
    import {SliceZone} from '@prismicio/vue';
    import type {TagTemplateDocument, ReinsuranceSliceSlice} from '@prismicio-types';
    import {createError, useAsyncData, useRoute, useSeoMeta, useWebsitePageView, useTranslation, useHead, useLogger, useRequestURL} from '#imports';
    import type {PaginationResponse, TagSearchResponse} from '@api';
    import {useTagPage} from '~/composables/acquisition/useAcquisitionPage';
    import {useABTestSlices} from '~/composables/useABTestSlices';
    import {TAG_PAGE} from '~/keys';
    import {components} from '~/slices/components';
    import TagPageFooter from './TagPageFooter.vue';
    import TagPageHero from './TagPageHero.vue';
    import TagPageProfiles from './TagPageProfiles.vue';

    const logger = useLogger();
    const {t} = useTranslation();
    const route = useRoute();

    const contentIsReady = ref(false);

    const slug = route.params.slug as string;
    const splits = slug.split('-');
    const identifier = ref(splits.pop() as string);
    const tag = ref(slug.replace('-' + identifier.value, ''));
    const slugDerivedTag = tag.value.replace('-', ' ').replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());

    // page parameter
    const page = ref(Number(route.query.p) || 1);

    const searchRequest = computed(() => ({
        tagName: tag.value,
        tagIdentifier: identifier.value,
        p: page.value,
    }));

    // prismicTemplate & landingPage
    const {fetchPrismicDocument, fetchLandingPage} = useTagPage();
    const {data, error} = await useAsyncData(
        'tag-page',
        () => Promise.allSettled([fetchLandingPage(searchRequest.value), fetchPrismicDocument('tagTemplate')]),
        {deep: true},
    );

    if (!data.value || data.value.length !== 2 || data.value[0].status === 'rejected' || error.value) {
        const statusMessage = `Error fetching tag page '${tag.value}'`;
        logger.error(
            new Error(statusMessage, {
                cause: JSON.stringify({
                    length: data.value?.length,
                    landingPageStatus: data.value?.[0].status,
                    prismicTemplateStatus: data.value?.[1].status,
                    maybeError: error.value,
                }),
            }),
        );
        throw createError({statusCode: 404, statusMessage});
    }

    const [landingPageOutcome, prismicTemplateOutcome] = data.value;
    const landingPageData = (landingPageOutcome as PromiseFulfilledResult<TagSearchResponse>).value;

    let prismicTemplate: TagTemplateDocument | null = null;
    if (prismicTemplateOutcome.status === 'rejected') {
        logger.info(`Could not load tagTemplate for tag '${tag.value}'. Continuing without Prismic template.`);
    } else {
        prismicTemplate = prismicTemplateOutcome.value as TagTemplateDocument;
    }

    const landingPage = computed(() => landingPageData);
    const humanReadableTag = computed<string>(() => landingPage.value?.tagDefinition?.name ?? slugDerivedTag);

    provide(TAG_PAGE, {
        prismicTemplate: prismicTemplate ?? undefined,
    });

    const {slices: templateSlices} = useABTestSlices(prismicTemplate as TagTemplateDocument);
    const reinsuranceSlice = computed<ReinsuranceSliceSlice | undefined>(() =>
        templateSlices.find((slice: Slice) => slice.slice_type === 'reinsurance_slice'),
    );

    const pagination = computed<PaginationResponse | undefined>(() => landingPage.value?.profileList?.pagination);
    const totalElements = computed<number | undefined>(() => pagination.value?.totalElements);
    const hasElements = computed<boolean>(() => typeof totalElements.value === 'number' && totalElements.value > 0);

    // prismicPage
    const uid = computed(() => searchRequest.value.tagName);
    const {data: prismicPage} = await useAsyncData('tag-page-prismic', () => fetchPrismicDocument('tagPage', uid.value), {deep: true});

    contentIsReady.value = true;

    // SEO
    useSeoMeta({
        ...(!hasElements.value && {
            robots: 'noindex',
        }),
        title: t('search.result.title', [humanReadableTag.value]),
        description: t('search.meta.description', [totalElements.value, humanReadableTag.value]),
    });

    // Canonical
    const {origin, pathname} = useRequestURL();
    const canonicalParams = route.query.p ? `?p=${route.query.p}` : '';
    const canonicalUrl = `${origin}${pathname}${canonicalParams}`;
    useHead({link: [{rel: 'canonical', href: canonicalUrl}]});

    // Tracking
    useWebsitePageView('tag-pages');
</script>

<style lang="scss">
    .tag-page__no-profiles {
        position: relative;
        text-align: center;
        font-weight: var(--joy-font-weight-bold);
        font-family: var(--joy-font-family-title);
        font-size: var(--joy-font-size-secondary-200);
    }
</style>
